// src/ui/ListEditorModal.jsx — create a list, or manage one that exists.
//
// One sheet, two modes. Creating: name it, then pick who's in it. Managing:
// rename it, add or remove people, or delete it outright. Everything writes
// users/{uid}/chatLists/{id}, the app's own collection, so the phone follows.
//
// People can be added two ways, and both end up as the same thing — a chat id:
//   • from your existing chats (the common case)
//   • by searching the directory by name, username, phone or email, which uses
//     the DETERMINISTIC 1:1 id, so someone you've never messaged can be in a
//     list and the list still works the moment you do message them.

import React, { useEffect, useMemo, useState } from 'react';
import Avatar, { resolveName, groupTitle } from './Avatar';
import ChatAvatar, { ChatTitle } from './ChatAvatar';
import { X, Search, Check, Trash } from './icons';
import { searchUsers } from '../services/users';
import { isNoteToSelf, otherParticipant } from '../services/chats';
import {
  createList, renameList, addToList, removeFromList, deleteList, chatIdFor,
} from '../services/chatLists';

const titleOf = (c, myUid) => (c.isGroup
  ? groupTitle(c)
  : (isNoteToSelf(c, myUid)
    ? 'Note to Self'
    : (c.participantNames?.[otherParticipant(c, myUid)] || 'Allo user')));

export default function ListEditorModal({ myUid, chats, list, initialIds = [], onClose, onCreated, onDeleted }) {
  const managing = !!list;
  const [name, setName] = useState(list?.name || '');
  const [ids, setIds] = useState(list?.chatIds || initialIds);
  const [term, setTerm] = useState('');
  const [people, setPeople] = useState([]);
  const [busy, setBusy] = useState(false);
  const [askDelete, setAskDelete] = useState(false);
  const [err, setErr] = useState('');

  // keep the local copy in step when the snapshot updates underneath us
  useEffect(() => { if (list) { setIds(list.chatIds || []); } }, [list?.id, (list?.chatIds || []).join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!term.trim()) { setPeople([]); return undefined; }
    let live = true;
    const t = setTimeout(() => {
      searchUsers(term, myUid).then((r) => live && setPeople(r)).catch(() => {});
    }, 250);
    return () => { live = false; clearTimeout(t); };
  }, [term, myUid]);

  const inList = useMemo(() => new Set(ids), [ids]);

  const chatMatches = useMemo(() => {
    const q = term.trim().toLowerCase();
    const rows = chats.filter((c) => !q || titleOf(c, myUid).toLowerCase().includes(q));
    return rows.slice(0, q ? 20 : 60);
  }, [chats, term, myUid]);

  /* ── writes ── */
  const toggleChat = async (chatId) => {
    if (!managing) {
      setIds((p) => (p.includes(chatId) ? p.filter((x) => x !== chatId) : [...p, chatId]));
      return;
    }
    setBusy(true);
    try {
      if (inList.has(chatId)) {
        const r = await removeFromList(myUid, list.id, chatId, ids);
        if (r === 'deleted') { onDeleted?.(list.id); onClose(); setBusy(false); return; }
        setIds((p) => p.filter((x) => x !== chatId));
      } else {
        await addToList(myUid, list.id, chatId);
        setIds((p) => [...p, chatId]);
      }
    } catch { setErr('That didn’t save — try again.'); }
    setBusy(false);
  };

  const save = async () => {
    if (!name.trim()) return;
    setBusy(true); setErr('');
    try {
      if (managing) {
        if (name.trim() !== list.name) await renameList(myUid, list.id, name);
        onClose();
      } else {
        if (!ids.length) { setErr('Add at least one chat.'); setBusy(false); return; }
        const id = await createList(myUid, name, ids);
        onCreated?.(id);
        onClose();
      }
    } catch { setErr('That didn’t save — try again.'); setBusy(false); }
  };

  const removeWhole = async () => {
    setBusy(true);
    try { await deleteList(myUid, list.id); onDeleted?.(list.id); onClose(); }
    catch { setErr('Couldn’t delete that list.'); setBusy(false); }
  };

  return (
    <div className="ng-modal" onClick={onClose}>
      <div className="ng-card le-card" onClick={(e) => e.stopPropagation()}>
        <div className="ng-head">
          <b>{managing ? 'Manage list' : 'New list'}</b>
          <button className="icon-btn" onClick={onClose} aria-label="Close"><X /></button>
        </div>

        <div className="ng-body">
          <input
            className="ng-input" value={name} maxLength={40} autoFocus={!managing}
            placeholder="List name — Family, Work, Football…"
            onChange={(e) => setName(e.target.value)}
          />
          {managing && name.trim() && name.trim() !== list.name && (
            <button className="ci-chip on le-rename" disabled={busy} onClick={save}>
              <Check width={12} height={12} /> Save name
            </button>
          )}

          <div className="le-count">
            {ids.length} {ids.length === 1 ? 'chat' : 'chats'} in this list
            {managing && <span className="le-live">changes save as you go</span>}
          </div>

          <div className="ng-search">
            <Search width={16} height={16} />
            <input
              value={term}
              placeholder="Search your chats, or anyone by name, username, phone or email"
              onChange={(e) => setTerm(e.target.value)}
            />
          </div>

          <div className="ng-results le-results">
            {/* your chats */}
            {chatMatches.length > 0 && <div className="sr-head">Your chats</div>}
            {chatMatches.map((c) => (
              <button
                className={`ng-row ${inList.has(c.id) ? 'on' : ''}`} key={c.id}
                disabled={busy} onClick={() => toggleChat(c.id)}
              >
                <ChatAvatar chat={c} myUid={myUid} size="sm" />
                <span className="ng-row-who">
                  <b><ChatTitle chat={c} myUid={myUid} /></b>
                  <i>{c.isGroup ? 'Group' : 'Chat'}</i>
                </span>
                {inList.has(c.id) && <Check width={15} height={15} />}
              </button>
            ))}

            {/* anyone else */}
            {people.length > 0 && <div className="sr-head">People</div>}
            {people.map((u) => {
              const cid = chatIdFor(myUid, u.uid);
              if (chats.some((c) => c.id === cid)) return null;   // already above
              return (
                <button
                  className={`ng-row ${inList.has(cid) ? 'on' : ''}`} key={u.uid}
                  disabled={busy} onClick={() => toggleChat(cid)}
                >
                  <Avatar profile={u} size="sm" myUid={myUid} />
                  <span className="ng-row-who">
                    <b>{resolveName(u)}</b>
                    <i>{u.username ? `@${u.username}` : (u.phoneE164 || u.email || '')}</i>
                  </span>
                  {inList.has(cid) && <Check width={15} height={15} />}
                </button>
              );
            })}

            {term.trim() && chatMatches.length === 0 && people.length === 0 && (
              <div className="ci-empty">Nobody found</div>
            )}
          </div>

          {err && <div className="pm-err">{err}</div>}

          {managing && (
            askDelete ? (
              <div className="ci-verify-ask">
                <span>Delete “{list.name}”? Your chats with these people and groups aren’t deleted.</span>
                <div>
                  <button className="ci-chip danger" disabled={busy} onClick={removeWhole}>
                    <Trash width={12} height={12} /> Delete list
                  </button>
                  <button className="ci-chip" onClick={() => setAskDelete(false)}>Cancel</button>
                </div>
              </div>
            ) : (
              <button className="le-delete" onClick={() => setAskDelete(true)}>
                <Trash width={14} height={14} /> Delete this list
              </button>
            )
          )}
        </div>

        <div className="ng-foot">
          <span className="ng-count">{managing ? 'Synced with your phone' : `${ids.length} selected`}</span>
          <button
            className="lg-go slim"
            disabled={busy || !name.trim() || (!managing && !ids.length)}
            onClick={managing ? onClose : save}
          >
            {managing ? 'Done' : (busy ? 'Creating…' : 'Create list')}
          </button>
        </div>
      </div>
    </div>
  );
}
